import { FC, useEffect, useMemo, useState } from 'react';
import agentsService from '@/services/agents.service';
import sectorsService from '@/services/sectors.service';
import { useAuthContext } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  Agent,
  AgentPublicationRequest,
  AgentScopeTarget,
  PublicSector,
} from '@/types/agents';
import LoadingSpinner from '@/components/LoadingSpinner';

const MIN_JUSTIFICATION = 30;

interface Props {
  agent: Agent;
  isOpen: boolean;
  onClose: () => void;
  onSubmitted: (req: AgentPublicationRequest) => void;
}

interface ApiError {
  response?: { status?: number; data?: { error?: string } };
}

function readErrorCode(err: unknown): string | null {
  return (err as ApiError)?.response?.data?.error ?? null;
}

const AgentShareModal: FC<Props> = ({ agent, isOpen, onClose, onSubmitted }) => {
  const { user } = useAuthContext();
  const { showToast } = useToast();
  const isLearner = user?.role === 'LEARNER';

  const [targetScope, setTargetScope] = useState<AgentScopeTarget>('SECTOR');
  const [sectors, setSectors] = useState<PublicSector[]>([]);
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [justification, setJustification] = useState('');
  const [loadingSectors, setLoadingSectors] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reseta o form ao abrir
  useEffect(() => {
    if (!isOpen) return;
    setTargetScope('SECTOR');
    setSelectedSectors([]);
    setJustification('');
  }, [isOpen]);

  // Carrega setores lazy ao abrir
  useEffect(() => {
    if (!isOpen) return;
    setLoadingSectors(true);
    sectorsService
      .list()
      .then(setSectors)
      .catch(() => showToast('Não foi possível carregar os setores.', 'error'))
      .finally(() => setLoadingSectors(false));
  }, [isOpen, showToast]);

  // Fecha com ESC
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const justificationLen = justification.trim().length;
  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (justificationLen < MIN_JUSTIFICATION) return false;
    if (targetScope === 'SECTOR' && selectedSectors.length === 0) return false;
    return true;
  }, [submitting, justificationLen, targetScope, selectedSectors]);

  const toggleSector = (id: string) => {
    setSelectedSectors((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const req = await agentsService.requestPublication(agent.id, {
        target_scope: targetScope,
        target_sectors: targetScope === 'SECTOR' ? selectedSectors : [],
        justification,
      });
      showToast('Pedido enviado! Aguarde aprovação do admin.', 'success');
      onSubmitted(req);
      onClose();
    } catch (err) {
      const code = readErrorCode(err);
      if (code === 'pending_request_exists') {
        showToast('Já existe um pedido pendente para esse agente.', 'error');
      } else if (code === 'learner_cant_request_global') {
        showToast('Apenas admins podem pedir publicação global.', 'error');
      } else if (code === 'not_owner') {
        showToast('Apenas o dono do agente pode compartilhá-lo.', 'error');
      } else {
        showToast('Não foi possível enviar o pedido.', 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-modal-title"
      data-testid="share-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-surface-card border border-surface-border shadow-2xl ring-1 ring-black/40 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between mb-4">
          <h2 id="share-modal-title" className="text-xl font-bold text-content-primary">
            Compartilhar agente
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="text-content-tertiary hover:text-content-primary p-1 rounded"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>

        <p className="text-sm text-content-secondary mb-4">
          Peça publicação de <strong className="text-content-primary">{agent.name}</strong> para
          que outras pessoas possam usá-lo. O admin precisa aprovar.
        </p>

        <fieldset className="mb-4">
          <legend className="text-sm font-semibold text-content-primary mb-2">Escopo</legend>
          <label className="flex items-center gap-2 mb-2 cursor-pointer">
            <input
              type="radio"
              name="target_scope"
              value="SECTOR"
              data-testid="scope-sector"
              checked={targetScope === 'SECTOR'}
              onChange={() => setTargetScope('SECTOR')}
              className="accent-accent"
            />
            <span className="text-sm text-content-secondary">
              <strong className="text-content-primary">Setor</strong> — apenas pessoas dos setores selecionados verão.
            </span>
          </label>
          {!isLearner && (
            <label className="flex items-center gap-2 cursor-pointer" data-testid="scope-global-label">
              <input
                type="radio"
                name="target_scope"
                value="GLOBAL"
                data-testid="scope-global"
                checked={targetScope === 'GLOBAL'}
                onChange={() => setTargetScope('GLOBAL')}
                className="accent-accent"
              />
              <span className="text-sm text-content-secondary">
                <strong className="text-content-primary">Global</strong> — qualquer pessoa na plataforma verá.
              </span>
            </label>
          )}
        </fieldset>

        {targetScope === 'SECTOR' && (
          <fieldset className="mb-4">
            <legend className="text-sm font-semibold text-content-primary mb-2">
              Setores ({selectedSectors.length} selecionado{selectedSectors.length === 1 ? '' : 's'})
            </legend>
            {loadingSectors && (
              <div className="flex items-center gap-2 text-sm text-content-tertiary">
                <LoadingSpinner size="small" /> Carregando setores…
              </div>
            )}
            {!loadingSectors && sectors.length === 0 && (
              <p className="text-sm text-content-tertiary">Nenhum setor cadastrado.</p>
            )}
            {!loadingSectors && sectors.length > 0 && (
              <div className="max-h-44 overflow-y-auto rounded-lg border border-surface-border bg-surface-secondary p-2">
                {sectors.map((s) => (
                  <label
                    key={s.id}
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-surface-hover rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      data-testid={`sector-checkbox-${s.id}`}
                      checked={selectedSectors.includes(s.id)}
                      onChange={() => toggleSector(s.id)}
                      className="accent-accent"
                    />
                    <span className="text-sm text-content-primary">{s.name}</span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>
        )}

        <fieldset className="mb-4">
          <legend className="text-sm font-semibold text-content-primary mb-2">
            Justificativa
          </legend>
          <textarea
            data-testid="justification-input"
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            rows={4}
            placeholder="Por que esse agente é útil pro time?"
            className="w-full resize-none rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-sm text-content-primary placeholder:text-content-tertiary focus:border-accent focus:outline-none"
          />
          <p
            className={`text-xs mt-1 ${
              justificationLen < MIN_JUSTIFICATION ? 'text-content-tertiary' : 'text-green-500'
            }`}
            data-testid="justification-counter"
          >
            {justificationLen}/{MIN_JUSTIFICATION} caracteres mínimos
          </p>
        </fieldset>

        <footer className="flex items-center justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm rounded-lg text-content-secondary hover:text-content-primary disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="share-submit"
            className="inline-flex items-center gap-1 bg-accent text-surface-primary font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <LoadingSpinner size="small" />
            ) : (
              <span className="material-symbols-outlined text-base">share</span>
            )}
            Enviar pedido
          </button>
        </footer>
      </div>
    </div>
  );
};

export default AgentShareModal;
